import React, { useState } from 'react';
import { BsFunnel, BsThreeDots, BsTrash } from 'react-icons/bs';
import CustomPageFilters from '../../components/ui/filter/PageFilters';
import TemplateTableWrapper from '../../layout/templates/TemplateTableWrapper';
import { UsersPageProps } from '../../types/page';
import { fields } from '../../utilities/dummy';
import data from '../../assets/json/applications.json'
import { 
    Box,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    FormLabel,
    Flex,
    Text
} from '@chakra-ui/react';
import { ApplicationDetail } from '../../types/applicationReducer';
import CustomModal from '../../components/ui/modals';
import FormInputText from "../../components/ui/forms/FormInputText";
import CustomDatePicker from '../../components/ui/date';
import { formatDateStringsToDate, formatDateToStrings } from '../../utilities/others';

export default function UsersPage(props: UsersPageProps) {
    const [modifiedData, setModifiedData] = useState<ApplicationDetail[]>(transformClientData())
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Partial<ApplicationDetail>>({});
    const [rowSelected, setRowSelected] = React.useState({})
    const currentDate = new Date();

    function transformClientData() {
        return data.map((item) => {
            return {
                ...item,
                createdAt: formatDateToStrings(item.createdAt, true)
            }
        })
    }

    function onEdit(data: Partial<ApplicationDetail>) {                
        setSelectedClient(data);
        setShowEditModal(true)
    }

    function handleUpdateClient() {
        const modifiedArr = data.map((item) => {            
            if (item.id === selectedClient.id) {
                return {
                    ...item,
                    ...selectedClient,
                }
            } else {
                return {
                    ...item,
                    createdAt: formatDateToStrings(item.createdAt, true)
                }
            }
        })        
        setModifiedData(modifiedArr)
        setShowEditModal(false);
        setSelectedClient({})
    }

    function openDeleteModal(data: Partial<ApplicationDetail>) {
        setSelectedClient(data);
        setShowDeleteModal(true)
    }

    function handleDeleteClient() {
        if (selectedClient?.id) {
            const updatedData = modifiedData.filter(item => item?.id !== selectedClient?.id);
            setModifiedData(updatedData)
            setShowDeleteModal(false)
        }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;

        setSelectedClient((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }

    function handleDateChange(date: any) {        
        setSelectedClient((prevState) => ({
            ...prevState,
            'createdAt': formatDateToStrings(date, true),
        }));
    }

    const companyColumns = React.useMemo(
        () => [
            {
                id: "companyName",
                header: () => <span>Company</span>,
                accessorKey: "companyName",
            },
            {
                id: "contactEmail",
                header: () => <span>Email</span>,
                accessorKey: "contactEmail",
            },
            {
                id: "addressLine2",
                header: () => <span>Address</span>,
                accessorKey: "addressLine2",
            },
            {
                id: "createdAt",
                header: () => <span>Date Added</span>,
                accessorKey: "createdAt",
            },
            {
                id: 'actionButtons',
                header: () =>  <span></span>,
                accessorKey: "actionButtons",
                cell: ({ row }: any) => (
                    <Menu>
                        <MenuButton 
                            as={IconButton}
                            aria-label='Table action button options'
                            icon={<BsThreeDots />}
                            variant='ghost'
                        />
                        <MenuList>
                            <MenuItem onClick={() => onEdit(row?.original)}>
                                Edit
                            </MenuItem>
                            <MenuItem onClick={() => openDeleteModal(row?.original)}>
                                Delete
                            </MenuItem>
                        </MenuList>
                    </Menu>
                )
            }
        ],
    []);

    function computeRowsText() {
        if (Object.keys(rowSelected)?.length === 1) {
            return `${Object.keys(rowSelected)?.length} row selected`
        } else {
            return `${Object.keys(rowSelected)?.length} rows selected`
        }
    }

    function handleSearch(val: string) {
        if (val === '') {
            setModifiedData(transformClientData())
        } else {            
            const result = modifiedData.filter(item => item.companyName.toLowerCase().includes(val));
            
            setModifiedData(result)
        }
    }
        
    return (
        <>
            {/* Filter section */}
                <Box paddingLeft='4' marginTop={'4'}>
                     <Flex alignItems={'center'} marginBottom={'6'}>
                        <Text color='gray.600' lineHeight='base' fontSize='md' fontWeight='bold'>
                            {computeRowsText()}
                        </Text>
                        <IconButton
                            variant='ghost'
                            color='gray.600'
                            aria-label='Delete rows'
                            fontSize='16px'
                            height={'auto'}
                            _hover={{ bg: 'transparent' }}
                            _focus={{ boxShadow: 'none', border: 'none', bg: 'transparent' }}
                            icon={<BsTrash />}
                        />
                    </Flex>

                    <CustomPageFilters 
                        title='users list'
                        isSearchDisabled={false}
                        fields={fields}
                        isVisible
                        isDate
                        isSearch
                        isOpen={isFilterOpen}
                        searchPlaceholder='Search users'
                        onOpen={() => setIsFilterOpen(true)}
                        onClose={() => setIsFilterOpen(false)}
                        buttons={[
                            {
                                label: 'Filter',
                                icon: <BsFunnel />,
                                isIcon: true,
                                disabled: false
                            },
                        ]}
                        isNewButton
                        addNewObj={{
                            pageType: 'user',
                        }}
                        isFilter
                        filterMode='multiple'
                        currentFilters={{}}
                        pageType='clients'
                        onSearch={handleSearch}
                    />
                </Box>
                {/* Table section */}
                <Box paddingTop={'1rem'}>
                    <TemplateTableWrapper 
                        data={modifiedData} 
                        columns={companyColumns} 
                        onRowSelected={setRowSelected}
                        rowSelection={rowSelected}
                    />
                </Box>

                {/* edit modal */}
                <CustomModal
                    isOpen={showEditModal}
                    onCloseFn={() => setShowEditModal(false)}
                    isFooter
                    size={'sm'}
                    headerTitle={'Edit client'}
                    footerSecondaryBtnLabel='Cancel'
                    footerPrimaryBtnLabel='Save'
                    footerPrimaryBtnFn={handleUpdateClient}
                >
                    <Box className="w--100" mb={'4'}>
                        <form>
                            <FormInputText 
                                id='companyName'
                                name='companyName'
                                label="Company Name"
                                placeholder='Enter client name'
                                value={selectedClient?.companyName!}
                                paddingLeft={'2'}
                                fontSize="sm"
                                formControlProps={{ marginBottom: '1rem' }}
                                onChange={handleChange}
                            /> 
                            <FormInputText 
                                id='contactEmail'
                                name='contactEmail'
                                label="Client Email"
                                placeholder='Enter client email'
                                value={selectedClient?.contactEmail!}
                                paddingLeft={'2'}
                                fontSize="sm"
                                formControlProps={{ marginBottom: '1rem' }}
                                onChange={handleChange}
                            />  
                            <FormInputText 
                                id='addressLine2'
                                name='addressLine2'
                                label="Client Address"
                                placeholder='Enter client address'
                                value={selectedClient?.addressLine2!}
                                paddingLeft={'2'}
                                fontSize="sm"
                                formControlProps={{ marginBottom: '1rem' }}
                                onChange={handleChange}
                            />  
                            <Box>
                                <FormLabel fontSize='sm' color='rgba(51, 65, 85, 1)' marginBottom={'0.5'} id='createdAt'>From</FormLabel>
                                <CustomDatePicker
                                    // onChange={(date) => handleDateChange(formatDateToStrings(date, true))}
                                    onChange={(date) => handleDateChange(date)}
                                    startDate={formatDateStringsToDate(selectedClient.createdAt!)}
                                    date={formatDateStringsToDate(selectedClient.createdAt!)}
                                    placeholder="Select date..."
                                    maxDate={currentDate}
                                />  
                            </Box>
                        </form>
                    </Box>
                </CustomModal>

            {/* delete client modal */}
            <CustomModal
                isOpen={showDeleteModal}
                onCloseFn={() => setShowDeleteModal(false)}
                isFooter
                size={'sm'}
                headerTitle={'Delete client'}
                footerSecondaryBtnLabel='Cancel'
                footerPrimaryBtnLabel='Delete'
                modalType='delete'
                footerPrimaryBtnFn={handleDeleteClient}
            >
                <Box className="w--100" mb={'4'}>
                   <Text color='gray.600' fontSize='md'>
                        Delete <strong>{selectedClient?.companyName}</strong> from your client list?
                    </Text>
                </Box>
            </CustomModal>
        </>
    )
}